import { Component, Input, OnInit, Output } from '@angular/core';
import {
	AbstractControl,
	UntypedFormControl,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import {
	MatList,
	MatListSubheaderCssMatStyler,
	MatListItem,
} from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';

enum StatusIcon {
	Unchecked = '', // Requirement is not satisfied
	Checked = 'check', // Requirement is met
}

interface PasswordRequirement {
	label: string;
	validator: ValidatorFn;
}

@Component({
	selector: 'app-password-validation',
	templateUrl: './password-validation.component.html',
	styleUrls: ['./password-validation.component.css'],
	imports: [MatList, MatListSubheaderCssMatStyler, MatListItem, MatIcon],
})
export class PasswordValidationComponent {
	@Input() passwordFormControl: UntypedFormControl = new UntypedFormControl(
		undefined
	);
	@Input() confirmPasswordFormControl: UntypedFormControl =
		new UntypedFormControl(undefined);

	requirements: PasswordRequirement[] = [
		{
			label: $localize`:@@passwordContainsAtLeast6characters:Contains at least 6 characters`,
			validator: Validators.minLength(6),
		},
		{
			label: $localize`:@@passwordContainsAtLeast1UppercaseLetter:Contains at least 1 uppercase letter`,
			validator: Validators.pattern(/[A-Z]/),
		},
		{
			label: $localize`:@@passwordContainsAtLeast1LowercaseLetter:Contains at least 1 lowercase letter`,
			validator: Validators.pattern(/[a-z]/),
		},
		{
			label: $localize`:@@passwordContainsAtLeast1NumericValue:Contains at least 1 numeric value`,
			validator: Validators.pattern(/[0-9]/),
		},
		{
			label: $localize`:@@passwordConfirmationPasswordMustMatch:Confirmation password must match`,
			validator: this.passwordMatchValidator.bind(this),
		},
	];

	constructor() {}

	// Validator that tests that password and confirm password matches
	private passwordMatchValidator(
		control: AbstractControl
	): { [key: string]: any } | null {
		if (
			control.value !== '' &&
			control.value === this.confirmPasswordFormControl.value
		) {
			return null;
		} else {
			return {
				passwordMatch: {
					password: control.value,
					confirmPassword: this.confirmPasswordFormControl.value,
				},
			};
		}
	}

	// Password is valid if all the password requirements pass validation
	passwordIsValid(): boolean {
		return this.requirements
			.map((e) => e.validator(this.passwordFormControl) === null)
			.reduce((value, next) => value && next, true);
	}

	validatePassword(validator: ValidatorFn): StatusIcon {
		if (this.passwordFormControl.value === '') {
			return StatusIcon.Unchecked;
		} else if (validator(this.passwordFormControl) === null) {
			return StatusIcon.Checked;
		} else {
			return StatusIcon.Unchecked;
		}
	}
}
